import { ForbiddenError } from './errors';

/**
 * Roles inside one client (docs/engineering/roles.md). director, manager, owner, occupant and vendor are memberships; "support"
 * is never stored: it is what a Syndiqia support person holds in a client during an access the client approved.
 */
export const ROLES = ['director', 'manager', 'owner', 'occupant', 'vendor', 'support'] as const;
export type Role = (typeof ROLES)[number];

/** Syndiqia staff roles: global, never memberships of a client (docs/engineering/roles.md). */
export const STAFF_ROLES = ['platform_admin', 'sales', 'support'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

/**
 * What an actor may do. Services check permissions, never roles: roles are only a way to grant a set of
 * permissions, so new roles (treasurer, auditor) or narrowly scoped agents need no change to the modules.
 */
export const PERMISSIONS = [
  // Registry
  'registry.configure', // buildings and units: set up once, at onboarding
  'registry.contacts.read', // co-owners' contact details and ownership records (Law 25: board only)
  'registry.contacts.write',
  'registry.ownership.write',
  'registry.quote_parts.write', // relative values come from the registered declaration
  // Ledger
  'ledger.read', // journal, co-owner accounts, arrears, bank: everyone's payments
  'ledger.configure', // chart of accounts and fiscal years
  'ledger.budget.write',
  'ledger.budget.adopt', // a board decision
  'ledger.calls.post',
  'ledger.payment.record',
  'ledger.entry.post',
  'ledger.entry.reverse',
  'ledger.bank.reconcile', // import statements and reconcile them
  // Comms
  'requests.create', // submit a request (repair, question, authorization...) and follow one's own
  'requests.manage', // see every request, reply, add internal notes, assign, change status
  'requests.read', // see every request and its thread, without replying (support access)
  'comms.log.read', // emails sent and their delivery status
  // Members
  'members.invite', // give co-owners and occupants access to the syndicate
  'members.roles', // make a member a director or a manager, or remove the role (directors; also checked in the database)
  // Documents
  'documents.read', // documents shared with members
  'documents.manage', // upload, delete, and see board-only documents
  // Automation
  'automation.view', // see the autonomy of each process and the log of automatic actions (the board)
  'automation.configure', // move a process between confirmation and automatic (directors)
  'onboarding.manage', // bring in what a new client has (documents, members, finances, suppliers) and apply it (directors)
  // Agents
  'agents.suggest', // the AI may record suggestions (drafts, classifications) for people to review
  'agents.ask', // ask questions answered from the syndicate's documents
  // Maintenance & compliance
  'maintenance.read', // work orders, components, vendors, logbook, calendar of obligations
  'maintenance.manage', // create and update them, record work done
  'maintenance.remind', // record that the board was reminded of an obligation (the reminders process)
  'maintenance.vendor', // what a contractor does through a work order's secure link (no account; the link's actor)
  'maintenance.invoice.receive', // record invoices received by email (the intake's system principal)
  // Governance
  'governance.view', // co-owners: the assemblies they are convened to, and their agenda
  'governance.read', // meetings of co-owners and of the board: agendas, notes, minutes, follow-ups
  'governance.manage', // prepare meetings, take notes, draft minutes
  'governance.approve', // approve the notice of meeting and the minutes: a board decision
  // Insurance
  'insurance.submit', // submit one's own insurance certificate and see one's own
  'insurance.manage', // every certificate, coverage by unit, review, reminders
  // Reservations
  'reservations.book', // book common facilities for one's own units
  'reservations.manage', // facilities and their rules, every booking, approvals
  // Collections
  'collections.prepare', // prepare payment reminders (people or the automatic process)
  'collections.approve', // approve sending them: a board decision
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const BOARD: Permission[] = [...PERMISSIONS];

/**
 * Directors (the board) decide; a contracted manager executes the day-to-day but does not adopt budgets,
 * change the declaration's relative values or reverse entries. Co-owners and occupants see the common
 * information (units, quote-parts) and submit and follow their own requests.
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  director: BOARD,
  manager: BOARD.filter(
    (p) => !['ledger.budget.adopt', 'ledger.entry.reverse', 'registry.quote_parts.write', 'ledger.configure', 'collections.approve', 'governance.approve', 'members.roles', 'automation.configure', 'onboarding.manage'].includes(p),
  ),
  owner: ['requests.create', 'documents.read', 'agents.ask', 'governance.view', 'reservations.book', 'insurance.submit'],
  occupant: ['requests.create', 'documents.read', 'agents.ask', 'reservations.book', 'insurance.submit'],
  vendor: [],
  // Syndiqia support during an access the client approved: reads what the board reads, changes nothing. Owners'
  // insurance certificates (personal documents) stay out of reach.
  support: ['registry.contacts.read', 'ledger.read', 'requests.read', 'comms.log.read', 'documents.read', 'governance.read', 'maintenance.read', 'automation.view'],
};

/**
 * Who is acting, in which tenant, with which roles. Humans and agents both act through an Actor.
 * A service principal (automatic process, agent) has no role but an explicit, narrow permission list.
 */
export interface Actor {
  readonly userId: string;
  readonly tenantId: string;
  readonly roles: readonly Role[];
  readonly permissions?: readonly Permission[];
  /** Set when Syndiqia staff act in a client: in a demo, or through an approved support access. */
  readonly staff?: { readonly mode: 'demo' | 'support' | 'onboarding'; readonly accessId?: string; readonly viewAs?: string; readonly staffUserId?: string };
}

export function permissionsOf(roles: readonly Role[]): Permission[] {
  return PERMISSIONS.filter((p) => roles.some((r) => ROLE_PERMISSIONS[r].includes(p)));
}

export function can(actor: Actor, permission: Permission): boolean {
  return actor.roles.some((r) => ROLE_PERMISSIONS[r].includes(permission)) || (actor.permissions?.includes(permission) ?? false);
}

/** The identity of automatic actions in a tenant ("Syndiqia (automatique)"), with exactly these permissions. */
export function systemActor(tenantId: string, permissions: readonly Permission[]): Actor {
  return { userId: SYSTEM_USER_ID, tenantId, roles: [], permissions };
}

export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000001';

export function requirePermission(actor: Actor, permission: Permission): void {
  if (!can(actor, permission)) throw new ForbiddenError(`Missing permission: ${permission}`);
}
